import { useEffect, useState } from "react";
import {
  api,
  type AdminUser,
  type InstanceGrantView,
  type OrgPrincipalAccess,
  type UserAccessView,
} from "../api/client";
import { BulkAccessDialog } from "./Admin";
import { KeyDistribution } from "./KeyDistribution";
import { grantAndDistribute } from "../vault/access-keys";
import { useVault } from "../vault/store";

// Access-visibility admin (§9). Read-first: shows WHO has access to WHAT and WHO can access
// everything, making the two axes explicit — authorization (a grant/capability) vs. decryption
// (holding the org key). Three views: Overview (instance-wide), Users (per-user), Organizations
// (per-org roster), Keys (authorized but holds no key — the one state that makes a vault
// invisible). Revoke is inline. Grant creation lives in the (separate) grant flow.

type View = "overview" | "users" | "orgs" | "keys";

export function AccessPanel() {
  const [view, setView] = useState<View>("overview");
  return (
    <div className="max-w-4xl">
      <GapBanner onOpen={() => setView("keys")} />
      <div className="mb-4 flex gap-2">
        {(
          [
            ["overview", "Overview"],
            ["users", "By user"],
            ["orgs", "By organization"],
            ["keys", "Keys"],
          ] as [View, string][]
        ).map(([v, label]) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`rounded-full border px-3 py-1 text-sm ${view === v ? "border-accent bg-accent/10 text-fg" : "border-border text-muted hover:border-accent"}`}
          >
            {label}
          </button>
        ))}
      </div>
      <Legend />
      {view === "overview" && <OverviewView />}
      {view === "users" && <UsersView />}
      {view === "orgs" && <OrgsView />}
      {view === "keys" && <KeyDistribution />}
    </div>
  );
}

// GapBanner surfaces the failure this panel exists for: someone is authorized but holds no vault
// key, so the vault does not appear for them at all. Without it the state is invisible — a role
// assigned to a group looks like it worked.
function GapBanner({ onOpen }: { onOpen: () => void }) {
  const [data] = useAsync(() => api.getAccessGaps(), []);
  if (!data || data.gaps.length === 0) return null;
  const users = new Set(data.gaps.map((g) => g.user_id)).size;
  return (
    <button
      onClick={onOpen}
      className="mb-4 flex w-full items-center gap-2 rounded-lg border border-warn/40 bg-warn/10 px-3 py-2 text-left text-sm text-warn hover:border-warn"
    >
      <span className="font-medium">⚠ {users} user(s) authorized without a vault key</span>
      <span className="text-xs opacity-80">
        — {data.gaps.length} vault assignment(s) have no effect until the key is handed over. Open “Keys”.
      </span>
    </button>
  );
}

function Legend() {
  return (
    <div className="mb-4 flex flex-wrap gap-3 text-[11px] text-muted">
      <span><Badge kind="admin" /> can manage the app (users/grants)</span>
      <span><Badge kind="all" /> authorized on all vault data</span>
      <span><Badge kind="nokey" /> authorized but cannot decrypt (no key)</span>
      <span><Badge kind="nogrant" /> holds key but not authorized</span>
      <span><Badge kind="bg" /> break-glass account</span>
    </div>
  );
}

function Badge({ kind, title }: { kind: "admin" | "all" | "nokey" | "nogrant" | "bg" | "ok"; title?: string }) {
  const map: Record<string, [string, string]> = {
    admin: ["App-admin", "bg-accent/20 text-accent"],
    all: ["All data", "bg-danger/20 text-danger"],
    nokey: ["⚠ no key", "bg-warn/20 text-warn"],
    nogrant: ["key, no grant", "bg-warn/20 text-warn"],
    bg: ["break-glass", "bg-danger/20 text-danger"],
    ok: ["✓", "bg-ok/20 text-ok"],
  };
  const [label, cls] = map[kind];
  return <span title={title} className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>{label}</span>;
}

function Yn({ v }: { v: boolean }) {
  return <span className={v ? "text-ok" : "text-muted"}>{v ? "✓" : "—"}</span>;
}

function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): [T | null, string | null, () => void] {
  const [data, setData] = useState<T | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    let live = true;
    fn()
      .then((d) => live && setData(d))
      .catch((e) => live && setErr(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n]);
  return [data, err, () => setN((x) => x + 1)];
}

// --- Overview ---

function OverviewView() {
  const [data, err, reload] = useAsync(() => api.getAccessOverview(), []);
  async function revoke(g: InstanceGrantView) {
    if (!window.confirm(`Revoke instance access for ${g.name}?`)) return;
    await api.revokeInstanceGrant(g.principal_id, g.principal_type);
    reload();
  }
  if (err) return <Err msg={err} />;
  if (!data) return <Loading />;
  return (
    <>
      <AddInstanceGrant onDone={reload} />
      <h3 className="mb-2 text-sm font-semibold">Instance-wide access (who can reach everything)</h3>
      {data.instance_grants.length === 0 ? (
        <p className="text-sm text-muted">No instance-level grants.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted">
            <tr><th className="py-1">Principal</th><th>Role</th><th>Flags</th><th></th></tr>
          </thead>
          <tbody>
            {data.instance_grants.map((g) => (
              <tr key={g.principal_id} className="border-t border-border">
                <td className="py-1.5">{g.name} <span className="text-xs text-muted">({g.principal_type})</span></td>
                <td>{g.role || "—"}</td>
                <td className="flex gap-1 py-1.5">
                  {g.is_app_admin && <Badge kind="admin" />}
                  {g.grants_all_data && <Badge kind="all" />}
                </td>
                <td className="text-right">
                  <button onClick={() => revoke(g)} className="rounded border border-border px-2 py-0.5 text-xs text-muted hover:border-danger hover:text-danger">Revoke</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <h3 className="mb-2 mt-6 text-sm font-semibold">Break-glass accounts</h3>
      {data.break_glass.length === 0 ? (
        <p className="text-sm text-muted">None.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {data.break_glass.map((u) => (
            <span key={u.id} className="rounded border border-border px-2 py-1 text-sm">{u.name} <Badge kind="bg" /></span>
          ))}
        </div>
      )}
    </>
  );
}

const INSTANCE_ROLES = ["owner_admin", "org_manager"];

function AddInstanceGrant({ onDone }: { onDone: () => void }) {
  const vault = useVault();
  const [users] = useAsync(() => api.listUsers(), []);
  const [uid, setUid] = useState("");
  const [role, setRole] = useState("owner_admin");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  async function grant() {
    if (!uid) return;
    setBusy(true);
    setLog([]);
    try {
      // Instance scope authorizes every vault, so the keys for every vault have to follow —
      // otherwise the user is "authorized on all data" and still sees an empty list.
      await grantAndDistribute({
        grant: { principal_type: "user", principal_id: uid, scope: "instance", role },
        userIds: [uid],
        orgKeyFor: vault.orgKeyFor,
        log: (l) => setLog((p) => [...p, l]),
      });
      setUid("");
      onDone();
    } catch (e) {
      setLog((p) => [...p, `✗ ${e instanceof Error ? e.message : String(e)}`]);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mb-5 flex flex-wrap items-end gap-2 rounded-lg border border-border bg-panel p-3">
      <div className="text-xs text-muted">Grant instance access (app-admin / all-data reach):</div>
      <select value={uid} onChange={(e) => setUid(e.target.value)} className="rounded border border-border bg-panel2 px-2 py-1 text-sm">
        <option value="">user…</option>
        {(users?.users ?? []).map((u: AdminUser) => <option key={u.id} value={u.id}>{u.login}</option>)}
      </select>
      <select value={role} onChange={(e) => setRole(e.target.value)} className="rounded border border-border bg-panel2 px-2 py-1 text-sm">
        {INSTANCE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <button
        onClick={grant}
        disabled={busy || !uid || vault.phase !== "unlocked"}
        title={vault.phase !== "unlocked" ? "Unlock the vault first — the keys are handed over from your browser" : undefined}
        className="rounded bg-accent px-3 py-1 text-sm font-medium text-white hover:bg-accent2 disabled:opacity-50"
      >
        {busy ? "Granting…" : "Grant + keys"}
      </button>
      {log.length > 0 && (
        <div className="max-h-32 w-full overflow-auto rounded bg-panel2 p-2 font-mono text-xs">
          {log.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      )}
    </div>
  );
}

// --- By user ---

function UsersView() {
  const [users] = useAsync(() => api.listUsers(), []);
  const [sel, setSel] = useState<string>("");
  const [granting, setGranting] = useState<AdminUser | null>(null);
  const [rev, setRev] = useState(0);
  const selUser = (users?.users ?? []).find((u) => u.id === sel) ?? null;
  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <select value={sel} onChange={(e) => setSel(e.target.value)} className="rounded border border-border bg-panel2 px-2 py-1.5 text-sm">
          <option value="">Select a user…</option>
          {(users?.users ?? []).map((u: AdminUser) => (
            <option key={u.id} value={u.id}>{u.login}</option>
          ))}
        </select>
        {selUser && (
          <button onClick={() => setGranting(selUser)} className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent2">
            Grant access…
          </button>
        )}
      </div>
      {sel && <UserAccessDetail key={rev} userId={sel} />}
      {granting && (
        <BulkAccessDialog
          user={granting}
          onClose={() => {
            setGranting(null);
            setRev((x) => x + 1);
          }}
        />
      )}
    </>
  );
}

function UserAccessDetail({ userId }: { userId: string }) {
  const [d, err] = useAsync<UserAccessView>(() => api.getUserAccess(userId), [userId]);
  if (err) return <Err msg={err} />;
  if (!d) return <Loading />;
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <span className="font-medium">{d.name}</span>
        {d.is_app_admin && <Badge kind="admin" />}
        {d.grants_all_data && <Badge kind="all" title="Instance grant with RevealSecret — authorized on every org" />}
        {d.instance_role && <span className="text-xs text-muted">instance: {d.instance_role}</span>}
      </div>
      {d.groups.length > 0 && <p className="mb-3 text-xs text-muted">Groups: {d.groups.join(", ")}</p>}
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted">
          <tr><th className="py-1">Organization</th><th>Authorized</th><th>Can decrypt</th><th>Role / caps</th></tr>
        </thead>
        <tbody>
          {d.orgs.length === 0 && <tr><td colSpan={4} className="py-2 text-muted">No org-scoped access.</td></tr>}
          {d.orgs.map((o) => (
            <tr key={o.org_id} className="border-t border-border">
              <td className="py-1.5">{o.name || o.org_id}</td>
              <td><Yn v={o.authorized} /></td>
              <td>
                <Yn v={o.can_decrypt} />
                {o.authorized && !o.can_decrypt && <> <Badge kind="nokey" /></>}
              </td>
              <td className="text-xs text-muted">{o.role || o.capabilities.length + " caps"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- By organization ---

function OrgsView() {
  const [orgs] = useAsync(() => api.listAllOrgs(), []);
  const [sel, setSel] = useState<string>("");
  return (
    <>
      <select value={sel} onChange={(e) => setSel(e.target.value)} className="mb-4 rounded border border-border bg-panel2 px-2 py-1.5 text-sm">
        <option value="">Select an organization…</option>
        {(orgs?.organizations ?? []).map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      {sel && <OrgAccessDetail orgId={sel} />}
    </>
  );
}

function OrgAccessDetail({ orgId }: { orgId: string }) {
  const [d, err, reload] = useAsync(() => api.getOrgAccess(orgId), [orgId]);
  if (err) return <Err msg={err} />;
  if (!d) return <Loading />;
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs text-muted">
        <tr><th className="py-1">Principal</th><th>Role / caps</th><th>Can decrypt</th><th>Status</th><th></th></tr>
      </thead>
      <tbody>
        {d.principals.length === 0 && <tr><td colSpan={5} className="py-2 text-muted">No access.</td></tr>}
        {d.principals.map((p: OrgPrincipalAccess) => (
          <tr key={p.principal_type + p.principal_id} className="border-t border-border">
            <td className="py-1.5">{p.name} <span className="text-xs text-muted">({p.principal_type})</span></td>
            <td className="text-xs text-muted">{p.role || (p.has_grant ? p.capabilities.length + " caps" : "—")}</td>
            <td><Yn v={p.can_decrypt} /></td>
            <td className="py-1.5">
              {p.has_grant && p.can_decrypt && <Badge kind="ok" title="Authorized and can decrypt" />}
              {p.has_grant && !p.can_decrypt && p.principal_type === "user" && <Badge kind="nokey" />}
              {!p.has_grant && p.can_decrypt && <Badge kind="nogrant" />}
            </td>
            <td className="text-right">
              <RevokeGrant p={p} onDone={reload} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RevokeGrant({ p, onDone }: { p: OrgPrincipalAccess; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  if (!p.has_grant || !p.grant_id) return null;
  async function revoke() {
    if (!window.confirm(`Revoke ${p.name}'s grant on this organization?`)) return;
    setBusy(true);
    try {
      await api.deleteGrant(p.grant_id!);
      onDone();
    } finally {
      setBusy(false);
    }
  }
  return (
    <button onClick={revoke} disabled={busy} className="rounded border border-border px-2 py-0.5 text-xs text-muted hover:border-danger hover:text-danger disabled:opacity-50">
      Revoke
    </button>
  );
}

function Loading() {
  return <p className="text-sm text-muted">Loading…</p>;
}
function Err({ msg }: { msg: string }) {
  return <p className="rounded border border-danger/40 bg-danger/10 p-2 text-sm text-danger">{msg}</p>;
}
